import React, { useState } from 'react';
import { ShoppingCart, Plus, Minus, Users } from 'lucide-react';
import CategoryIcon from '../CategoryIcon.jsx';
import { accusativeUnit } from '../utils';

/**
 * ProductImage - компонент для отображения изображения товара
 * Скопирован из catalog.jsx для консистентности
 */
const ProductImage = ({ product, size = 'catalog', className = '' }) => {
  const [imageError, setImageError] = useState(false);

  // Заглушка - иконка категории (БЕЗ backdrop-filter для производительности)
  const placeholder = (
    <div className={`w-full h-full bg-gradient-to-br from-cyan-500/[0.35] to-blue-600/[0.45] flex items-center justify-center ${className}`}>
      <CategoryIcon category={product.category_name} size={size === 'catalog' ? 'large' : 'xlarge'} />
    </div>
  );

  // Если есть URL изображения и оно загружается без ошибок
  if (product.image_url && !imageError) {
    return (
      <img
        src={product.image_url}
        alt={product.product_name}
        className={`w-full h-full object-contain ${className}`}
        loading="lazy"
        onError={() => setImageError(true)}
      />
    );
  }

  // Иначе заглушка
  return placeholder;
};

/**
 * Гибридная карточка товара - ТОЧНАЯ КОПИЯ из catalog.jsx
 *
 * Скопирована структура из catalog.jsx (строки 905-1009) с адаптацией для HomePage:
 * - Точно те же Tailwind классы + inline styles
 * - Фиксированная ширина для горизонтального скролла
 * - Два слоя стекла поверх изображения
 * - backdrop-blur-lg для контента
 * - Одна кнопка "Подробнее"
 */
const ProductCardHybrid = ({
  product,
  onAddToCart,
  onUpdateQuantity,
  onRemoveFromCart,
  cartQuantity = 0,
  onViewDetails,
  isLast = false,
  gridMode = false
}) => {
  const cardStyle = gridMode
    ? {
        boxShadow: '0 8px 24px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
        borderRadius: '16px',
        WebkitBorderRadius: '16px',
        MozBorderRadius: '16px',
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
        contain: 'layout style paint'
      }
    : {
        flex: '0 0 clamp(200px, 45vw, 280px)',
        width: 'clamp(200px, 45vw, 280px)',
        boxShadow: '0 8px 24px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
        borderRadius: '16px',
        WebkitBorderRadius: '16px',
        MozBorderRadius: '16px',
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
        contain: 'layout style paint'
      };

  return (
    <div
      key={product.id}
      onClick={() => onViewDetails(product)}
      role="button"
      tabIndex={0}
      aria-label={`Подробнее о товаре ${product.name || product.product_name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewDetails(product);
        }
      }}
      className="product-card-optimized relative bg-gradient-to-br from-cyan-50/[0.30] to-blue-50/[0.15] dark:from-cyan-400/[0.30] dark:to-blue-400/[0.15] rounded-2xl border border-gray-200/40 dark:border-white/[0.12] shadow-[0_2px_8px_rgba(14,165,233,0.08)] overflow-hidden transition-all hover:shadow-[0_16px_48px_rgba(99,102,241,0.25),inset_0_1px_0_rgba(255,255,255,0.2)] hover:-translate-y-1 flex flex-col cursor-pointer"
      style={cardStyle}
    >
      {/* Изображение товара - за стеклом (тонкий эффект) */}
      <div className="aspect-square relative overflow-hidden">
        <ProductImage product={product} size="catalog" />

        {/* Легкий градиент поверх изображения (БЕЗ backdrop-filter для производительности) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06), transparent)',
            mixBlendMode: 'overlay'
          }}
        />

        {/* Pack Share Badge - совместная покупка */}
        {product.share_interest_count > 0 && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            display: 'flex', alignItems: 'center', gap: 3,
            background: 'rgba(14,165,233,0.85)', backdropFilter: 'blur(8px)',
            color: 'white', padding: '3px 8px', borderRadius: 8,
            fontSize: 11, fontWeight: 600, zIndex: 2,
          }}>
            <Users size={12} />
            {product.share_interest_count}
          </div>
        )}

        {/* Avatar Stack - заказавшие (слева внизу, стиль progress bar) */}
        {product.buyersCount > 0 && product.buyersInitials?.length > 0 && (
          <div className="avatar-stack-v2">
            {product.buyersInitials.slice(0, 3).map((initial, i) => (
              <div key={i} className="avatar-v2" style={{ zIndex: 3 - i }}>
                <span>{initial}</span>
              </div>
            ))}
            <span className="avatar-label">{product.buyersCount === 1 ? 'заказал' : 'заказали'} {product.buyersCount}</span>
          </div>
        )}
      </div>

      {/* Информация о товаре - статичный фон для производительности */}
      <div className="p-4 flex flex-col flex-grow bg-white/85 dark:bg-gray-800/85">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2 mb-2">
          {product.name || product.product_name}
        </h3>

        <div className="mt-auto space-y-3">
          <div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {Math.round(product.basePricePerKg || product.retailPrice || product.price || 0).toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              за {accusativeUnit(product.unit || 'шт')}
            </div>
          </div>

          {/* Кнопка корзины / степпер количества */}
          {cartQuantity > 0 ? (
            <div className="card-qty-stepper" onClick={(e) => e.stopPropagation()}>
              <button
                className="qty-btn qty-btn-minus"
                aria-label="Уменьшить количество"
                onClick={() => {
                  if (cartQuantity <= 1) {
                    onRemoveFromCart && onRemoveFromCart(product.id);
                  } else {
                    onUpdateQuantity && onUpdateQuantity(product.id, cartQuantity - 1);
                  }
                }}
              >
                <Minus size={15} strokeWidth={2.5} />
              </button>
              <span className="qty-value">{cartQuantity}</span>
              <button
                className="qty-btn qty-btn-plus"
                aria-label="Увеличить количество"
                onClick={() => {
                  onUpdateQuantity && onUpdateQuantity(product.id, cartQuantity + 1);
                }}
              >
                <Plus size={15} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <button
              className="card-cart-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(product, product.min_order_quantity || 1);
              }}
            >
              <ShoppingCart size={16} strokeWidth={2} />
              <span>В корзину</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Оптимизация: React.memo предотвращает лишние ре-рендеры
export default React.memo(ProductCardHybrid);
